import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from "react-redux";
import NavbarMob from '../components/NavbarMob';
import Footer from '../components/Footer';
import FooterMob from '../components/FooterMob';
import banner1 from '../images/Home/banner2.webp';
import bannermob from '../images/Home/bannermob2.webp';
import { useStore } from '../useStore';
import line from '../images/Home/line.png';
import product1 from '../images/Home/product2.webp';
import heart from '../images/Home/heart.png';
import filledHeart from '../images/Home/heart2.png';
import men from '../images/Home/MEN2.png';
import women from '../images/Home/WOMEN2.png';
import button from '../images/Home/button.png';
import combined from '../images/Home/groupFootr.jpeg';
import star from '../images/Home/Star.png';
import starmob from '../images/Home/startmob.png';
import { toast } from 'react-toastify';

const PromotionTags = ({ product }) => {
    return (
        <div className="flex flex-wrap gap-1 mb-1">
            {product.BestSeller === 1 && (
                <span className="bg-orange-100 text-orange-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Best Seller
                </span>
            )}
            {product.LimitedSale === 1 && (
                <span className="bg-rose-100 text-rose-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Limited Sale
                </span>
            )}
            {product.NewArrivals === 1 && (
                <span className="bg-sky-100 text-sky-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    New Arrival
                </span>
            )}
        </div>
    );
};

function Home() {
    const categoryImages = useSelector((state) => state.category.images);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const isTab = useMediaQuery({ query: '(max-width: 1024px)' });

    const [categories, setCategories] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [startIndex, setStartIndex] = useState(0);
    const [disableNext, setDisableNext] = useState(false);
    const [disablePrev, setDisablePrev] = useState(true);
    const [loginId, setLoginId] = useState(null);
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);

    // console.log(allProducts, 'allProducts');


    // Pagination states
    const [nextRowOffset, setNextRowOffset] = useState(0); // New cursor state
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [totalProducts, setTotalProducts] = useState(0);
    const [visibleProducts, setVisibleProducts] = useState(new Set());
    const [banners, setBanners] = useState([]);
    const [loadingBanners, setLoadingBanners] = useState(true);
    const PAGE_SIZE = 20;

    const shopByCategoryRef = useRef(null);
    const abortControllerRef = useRef(null);
    const mountedRef = useRef(true);

    const { showDropdown, setShowDropdown } = useStore();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;
    const userId = useSelector((state) => state.user.id);
    const navigate = useNavigate();

    // mountedRef for cleanup
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Get loginId from localStorage
    useEffect(() => {
        const storedLoginId = localStorage.getItem('loginId');
        setLoginId(storedLoginId);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Load more products function (technique from SearchPage)
    const loadMoreProducts = async () => {
        if (!apiBaseUrl || isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        const offsetParam = nextRowOffset ? `&nextRowOffset=${nextRowOffset}` : `&page=2`;

        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const signal = controller.signal;

        try {
            const response = await fetch(
                `${apiBaseUrl}/getProductByFilter/BLACKBATON_2526?${offsetParam}&pageSize=${PAGE_SIZE}`,
                { signal }
            );
            const result = await response.json();

            if (result.products && Array.isArray(result.products)) {
                // Determine new products
                const newProducts = result.products.filter(newProd =>
                    !allProducts.some(p => p.ID === newProd.ID)
                );

                // Always update pagination state from backend (Fix for Infinite Scroll loop)
                setNextRowOffset(result.pagination?.nextRowOffset || 0);
                setHasMore(result.pagination?.hasMore || false);
                setTotalProducts(result.pagination?.totalProducts || 0);

                if (newProducts.length > 0) {
                    setAllProducts(prev => [...prev, ...newProducts]);

                    // Stagger visibility for animation
                    // Instant visibility
                    setVisibleProducts(prev => {
                        const newSet = new Set(prev);
                        newProducts.forEach(p => newSet.add(p.ID));
                        return newSet;
                    });
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('Error loading more products:', error);
        } finally {
            if (mountedRef.current && !signal.aborted) setIsLoadingMore(false);
        }
    };

    // Fetch initial data (Categories and New Arrivals)
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!apiBaseUrl) return;

            try {
                // Fetch Categories
                const catRes = await fetch(`${apiBaseUrl}/getCategories/BLACKBATON_2526`);
                const catData = await catRes.json();
                if (mountedRef.current) setCategories(Array.isArray(catData) ? catData : []);

                // Fetch Banners
                const bannerRes = await fetch(`${apiBaseUrl}/getBanners/BLACKBATON_2526`);
                const bannerData = await bannerRes.json();
                if (mountedRef.current) {
                    setBanners(Array.isArray(bannerData) ? bannerData : []);
                    setLoadingBanners(false);
                }

                // Fetch New Arrivals (Limit 4 for Home Page) - Add skipCount=true for speed
                const arrivalRes = await fetch(`${apiBaseUrl}/getNewArrival_BestSeller_LimitedSale/BLACKBATON_2526?type=NEW ARRIVAL&pageSize=4&skipCount=true`);
                const arrivalData = await arrivalRes.json();
                if (arrivalData.products && mountedRef.current) {
                    setNewArrivals(arrivalData.products);
                }
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };

        fetchInitialData();
    }, [apiBaseUrl]);

    // Fetch initial products with pagination
    useEffect(() => {
        const fetchInitialProducts = async () => {
            if (!apiBaseUrl) return;

            setLoadingProducts(true);
            setAllProducts([]);
            setNextRowOffset(0);
            setHasMore(true);

            if (abortControllerRef.current) abortControllerRef.current.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;
            const signal = controller.signal;

            try {
                // Initial load: page=1, nextRowOffset=0
                const response = await fetch(
                    `${apiBaseUrl}/getProductByFilter/BLACKBATON_2526?filter=&page=1&pageSize=${PAGE_SIZE}`,
                    { signal }
                );
                const result = await response.json();

                if (result.products && Array.isArray(result.products)) {
                    setAllProducts(result.products);


                    // Set pagination info
                    setNextRowOffset(result.pagination?.nextRowOffset || 0);
                    setHasMore(result.pagination?.hasMore || false);
                    setTotalProducts(result.pagination?.totalProducts || 0);

                    // Stagger visibility for animation
                    // Instant visibility
                    setVisibleProducts(prev => {
                        const newSet = new Set(prev);
                        result.products.forEach(p => newSet.add(p.ID));
                        return newSet;
                    });
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error fetching products:', error);
            } finally {
                if (mountedRef.current && !signal.aborted) setLoadingProducts(false);
            }
        };

        fetchInitialProducts();
    }, [apiBaseUrl]);



    // Infinite scrolling listener - REMOVED
    // useEffect(() => {
    //     const handleScroll = () => {
    //         if (!isLoadingMore && hasMore && allProducts.length > 0) {
    //             if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1000) {
    //                 loadMoreProducts();
    //             }
    //         }
    //     };
    //     // ...
    // }, [isLoadingMore, hasMore, allProducts.length, nextRowOffset]);


    // Fetch wishlist items
    useEffect(() => {
        const fetchWishlistItems = async () => {
            try {
                const activeUserId = loginId || userId;
                if (!activeUserId) return;

                const response = await fetch(`${apiBaseUrl}/wishlist/items/BLACKBATON_2526/${activeUserId}`);
                const data = await response.json();

                if (data.success && mountedRef.current) {
                    setWishlistItems(data.items);
                }
            } catch (error) {
                console.error("Error fetching wishlist items:", error);
            }
        };
        fetchWishlistItems();
    }, [loginId, apiBaseUrl, userId]);

    // Wishlist handlers
    const addToWishlist = async (product) => {
        const apiUrl = `${apiBaseUrl}/wishlist/add/BLACKBATON_2526`;
        const activeUserId = loginId || userId;
        if (!activeUserId) {
            toast.info("Please Login First to Continue.");
            return;
        }

        const payload = {
            ID: product.ID,
            ItemName: product.ItemName,
            MRP: product.MRP,
            ledcode: activeUserId,
            Qty: product.Qty,
            UniqueCode: product.UniqueCode,
            DisplayFirst: product.displayFirst
        };

        try {
            const res = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setWishlistItems(prev => [...prev, product]);
                toast.info("Item added to wishlist!");
            } else {
                toast.info("Already in Wishlist");
            }
        } catch (error) {
            toast.info("An error occurred while adding to wishlist.");
        }
    };

    const removeFromWishlist = async (productId) => {
        try {
            const res = await fetch(`${apiBaseUrl}/wishlist/delete/BLACKBATON_2526/${productId}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                setWishlistItems(prev => prev.filter(item => item.ID !== productId));
            }
        } catch (error) {
            console.error('Error removing from wishlist:', error);
        }
    };

    // Navigation handlers
    const fullimage = (id, itemName) => {
        navigate(`/fullimage?I=${btoa(String(id))}&N=${btoa(itemName)}`, { state: { id, itemName } });
    };

    const productPage = (categoryId, categoryName) => {
        navigate('/products', { state: { categoryId, categoryName } });
    };

    const newarrival = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 163, categoryName: 'NEW ARRIVAL' } });
    };

    const searchpage = () => {
        navigate('/searchpage', { state: { filtered: '' } });
    };

    const mensubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 627, categoryName: "MEN'S" } });
    };

    const womensubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 628, categoryName: "WOMEN'S" } });
    };

    const accessoriessubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 301, categoryName: "ACCESSORIES" } });
    };

    const kidssubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 629, categoryName: "KID'S" } });
    };

    // Category slider functions
    const nextSlide = () => {
        if (startIndex < categories.length - 3) {
            setStartIndex(prevIndex => prevIndex + 1);
            setDisablePrev(false);
            if (startIndex + 1 >= categories.length - 3) {
                setDisableNext(true);
            }
        }
    };

    const prevSlide = () => {
        if (startIndex > 0) {
            setStartIndex(prevIndex => prevIndex - 1);
            setDisableNext(false);
            if (startIndex - 1 === 0) {
                setDisablePrev(true);
            }
        }
    };

    // Get image URL with fallback
    const getProductImageUrl = (product) => {
        if (product.displayFirst) {
            return `${apiLocalUrl}/uploads/${product.displayFirst}.webp`;
        }
        return product1;
    };



    return (
        <div className='min-h-screen flex flex-col' onClick={() => setShowDropdown(false)}>
            {isMobile ? <NavbarMob /> : <Navbar />}

            {/* Banner Section */}
            <div className='w-full h-screen mt-0 md:mt-[60px]'>

                {/* {loadingBanners ? (
                    <div className="w-full h-full bg-gray-500 animate-pulse"></div>
                ) : (
                    <img
                        src={banners.find(b => b.banner === 'main')?.imgBase64 ? `data:image/jpeg;base64,${banners.find(b => b.banner === 'main').imgBase64}` : banner1}
                        alt="main"
                        className="w-full h-full md:block hidden object-cover"
                    />
                )} */}
                {/* <img
                    src={banners.find(b => b.banner === 'main')?.imgBase64 ? `data:image/jpeg;base64,${banners.find(b => b.banner === 'main').imgBase64}` : banner1}
                    alt="banner1"
                    className="w-full h-full md:block hidden object-cover"
                /> */}

                <img src={banner1} alt="banner1" className='w-full h-full md:block hidden object-cover' />
                <img src={bannermob} alt="bannermob" className='w-full h-full md:hidden object-cover' />

                {/* <img
                    src={banners.find(b => b.banner === 'main')?.imgBase64 ? `data:image/jpeg;base64,${banners.find(b => b.banner === 'main').imgBase64}` : banner1}
                    alt="bannermob"
                    className="w-full h-full md:hidden object-cover"
                /> */}

                <div className="absolute top-0 left-0 w-full h-full flex flex-col gap-3 items-center justify-center">
                    <span className="text-white lg:text-6xl text-3xl font-[800] font-montserrat text-center">
                        ELEVATE YOUR<br /> GAME, STYLE ON!
                    </span>
                    <span className="text-white text-xl font-[500] font-montserrat lg:block hidden text-center">
                        Discover cutting-edge apparel and accessories for every sport,
                        crafted for<br /> comfort, style, and unstoppable performance
                    </span>
                    <span className="text-white text-sm font-[500] font-montserrat lg:hidden text-center">
                        Discover cutting-edge apparel and accessories for<br /> every sport,
                        crafted for comfort, style,<br /> and unstoppable performance
                    </span>
                </div>
            </div>

            {/* Main Content */}
            <div className='w-full h-auto lg:px-12 px-3 lg:gap-12 gap-6 lg:pt-12 pt-6 flex flex-col' ref={shopByCategoryRef}>

                {/* Shop By Category */}
                <div className='flex flex-col gap-2 mt-3'>
                    <div className='flex flex-row justify-between w-full'>
                        <div className='flex flex-row gap-4 items-center'>
                            <img src={line} alt="line" />
                            <span className='lg:text-xl text-base font-[600] font-montserrat'>Shop By Category</span>
                        </div>
                        <div className='flex flex-row gap-2 items-center'>
                            <div
                                className={`w-[41px] h-[41px] bg-[#F5F5F5] rounded-full flex justify-center items-center font-bold cursor-pointer ${disablePrev ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={prevSlide}
                            >
                                {"<"}
                            </div>
                            <div
                                className={`w-[41px] h-[41px] bg-[#F5F5F5] rounded-full flex justify-center items-center font-bold cursor-pointer ${disableNext ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={nextSlide}
                            >
                                {">"}
                            </div>
                        </div>
                    </div>

                    {/* Desktop Categories */}
                    <div className='grid-cols-3 w-full gap-2 md:grid hidden'>
                        {(Array.isArray(categories) ? categories : []).slice(startIndex, startIndex + 3).map((category) => (
                            <div key={category.Id} className="lg:h-[280px] h-[250px] relative">
                                {/* {loadingBanners ? (
                                    <div className="w-full h-full bg-gray-100 animate-pulse"></div>
                                ) : (
                                    <img
                                        src={categoryImages[category.Id]}
                                        alt={category.Name}
                                        className="w-full h-full object-cover"
                                    />
                                )} */}
                                <img
                                    src={categoryImages[category.Id]}
                                    alt={category.Name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-0 left-0 w-full h-full px-12 py-12 flex items-end">
                                    <div
                                        className="w-auto h-[35px] bg-[white] hover:bg-[red] hover:text-[white] rounded-2xl px-5 flex justify-center items-center text-base font-[600] font-montserrat cursor-pointer transition-colors duration-300"
                                        onClick={() => productPage(category.Id, category.Name)}
                                    >
                                        {category.Name}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile Categories */}
                    <div className='grid grid-cols-1 w-full gap-2 md:hidden'>
                        {(Array.isArray(categories) ? categories : []).slice(startIndex, startIndex + 1).map((category) => (
                            <div key={category.Id} className="lg:h-[280px] h-[250px] relative">
                                {/* {loadingBanners ? (
                                    <div className="w-full h-full bg-gray-100 animate-pulse"></div>
                                ) : (
                                    <img
                                        src={categoryImages[category.Id]}
                                        alt={category.Name}
                                        className="w-full h-full object-cover"
                                    />
                                )} */}
                                <img
                                    src={categoryImages[category.Id]}
                                    alt={category.Name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-0 left-0 w-full h-full px-12 py-12 flex items-end">
                                    <div
                                        className="w-auto h-[35px] bg-[white] hover:bg-[red] hover:text-[white] rounded-2xl px-5 flex justify-center items-center text-base font-[600] font-montserrat cursor-pointer transition-colors duration-300"
                                        onClick={() => productPage(category.Id, category.Name)}
                                    >
                                        {category.Name}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* New Arrivals */}
                {newArrivals.length > 0 && (
                    <div className='flex flex-col gap-6 mt-3'>
                        <div className='flex flex-row w-full'>
                            <div className='flex flex-row gap-4 items-center'>
                                <img src={line} alt="line" />
                                <span className='lg:text-xl text-base font-[600] font-montserrat'>New Arrivals</span>
                            </div>
                        </div>
                        <div className='grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 w-full gap-x-5 gap-y-10'>
                            {newArrivals.map((product, index) => (
                                <div
                                    key={product.ID}
                                    className='flex flex-col gap-3 group'
                                    onClick={() => fullimage(product.ID, product.ItemName)}
                                >
                                    <div className='aspect-[3/4] rounded-2xl bg-gray-50 flex items-center justify-center relative overflow-hidden'>
                                        <img
                                            src={`${apiLocalUrl}/uploads/${product.displayFirst}.webp`}
                                            alt={product.ItemName}
                                            loading="lazy"
                                            onError={(e) => { e.target.src = product1; }}
                                            className='w-full h-full object-cover transition-transform duration-400 group-hover:scale-110'
                                        />
                                        <div className='absolute top-4 right-4'>
                                            <button
                                                className='w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex justify-center items-center cursor-pointer shadow-sm hover:bg-white transition-all transform hover:scale-110 active:scale-90'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (wishlistItems.some(item => item.ID === product.ID)) {
                                                        removeFromWishlist(product.ID);
                                                    } else {
                                                        addToWishlist(product);
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={wishlistItems.some(item => item.ID === product.ID) ? filledHeart : heart}
                                                    alt="heart"
                                                    className='w-5 h-5'
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='flex flex-col gap-1 px-1'>
                                        <PromotionTags product={product} />
                                        <h3 className='text-sm font-bold text-left text-gray-900 line-clamp-1 group-hover:text-red-600 transition-colors uppercase'>
                                            {product.ItemName}
                                        </h3>
                                        <div className='flex items-center gap-2'>
                                            <span className='text-lg font-black text-black'>Rs. {product.MRP}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='w-full flex items-end justify-end'>
                            <span
                                className='text-right underline cursor-pointer hover:text-red-600 text-base font-[500] font-montserrat transition-colors duration-300'
                                onClick={newarrival}
                            >
                                See All
                            </span>
                        </div>
                    </div>
                )}

                {/* Men's & Women's Sections */}
                <div className='w-full flex md:flex-row flex-col lg:gap-0 gap-3 lg:h-[500px] h-auto mt-3'>
                    <div className='lg:w-[50%] w-full lg:h-full md:h-[450px] h-[284px] relative'>

                        {loadingBanners ? (
                            <div className="w-full h-full bg-gray-100 animate-pulse"></div>
                        ) : (
                            <img
                                src={banners.find(b => b.banner === 'mens')?.imgBase64 ? `data:image/jpeg;base64,${banners.find(b => b.banner === 'mens').imgBase64}` : men}
                                alt="mens"
                                className="w-full h-full object-cover"
                            />
                        )}

                        {/* <img src={men} alt="men" className='w-full h-full object-cover' /> */}
                        <div className='absolute top-0 left-0 w-full h-full flex flex-col lg:gap-3 gap-2 justify-center items-center'>
                            <div className='w-auto lg:px-3 px-2 h-[57px] bg-black flex justify-center items-center'>
                                <span className='lg:text-5xl text-2xl font-[800] font-montserrat text-white'>Men's</span>
                            </div>
                            <img
                                src={button}
                                alt="button"
                                className='cursor-pointer hover:scale-105 transition-transform duration-300'
                                onClick={mensubcategorypage}
                            />
                        </div>
                    </div>
                    <div className='lg:w-[50%] w-full lg:h-full md:h-[450px] h-[284px] relative'>
                        {loadingBanners ? (
                            <div className="w-full h-full bg-gray-100 animate-pulse"></div>
                        ) : (
                            <img
                                src={banners.find(b => b.banner === 'womens')?.imgBase64 ? `data:image/jpeg;base64,${banners.find(b => b.banner === 'womens').imgBase64}` : women}
                                alt="womens"
                                className="w-full h-full object-cover"
                            />
                        )}
                        {/* <img src={women} alt="women" className='w-full h-full object-cover' /> */}
                        <div className='absolute top-0 left-0 w-full h-full flex flex-col lg:gap-3 gap-2 justify-center items-center'>
                            <div className='w-auto lg:px-3 px-2 h-[57px] bg-black flex justify-center items-center'>
                                <span className='lg:text-5xl text-2xl font-[800] font-montserrat text-white'>Women's</span>
                            </div>
                            <img
                                src={button}
                                alt="button"
                                className='cursor-pointer hover:scale-105 transition-transform duration-300'
                                onClick={womensubcategorypage}
                            />
                        </div>
                    </div>
                </div>

                {/* All Products */}
                <div className='flex flex-col gap-6 mt-3'>
                    <div className='flex flex-row w-full'>
                        <div className='w-full text-nowrap flex flex-row gap-4 items-center'>
                            <img src={line} alt="line" />
                            <span className='w-full lg:text-xl text-base text-left font-[600] font-montserrat'>ALL PRODUCTS</span>
                            {/* {allProducts.length > 0 && (
                                <span className="text-sm text-gray-500 ml-2">
                                    ({allProducts.length} of {totalProducts} products)
                                </span>
                            )} */}
                            <span
                                className='w-max text-right underline cursor-pointer hover:text-red-600 text-base font-[500] font-montserrat transition-colors duration-300'
                                onClick={searchpage}
                            >
                                See All
                            </span>
                        </div>
                    </div>

                    {loadingProducts ? (
                        <div className="grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 w-full gap-x-5 gap-y-10">
                            {Array.from({ length: 4 }).map((_, index) => {
                                const opacity = 1 - index * 0.2;      // visual depth
                                const delay = index * 400;            // delay per item (ms)

                                return (
                                    <div
                                        key={`skeleton-${index}`}
                                        className="flex flex-col gap-4 opacity-0 animate-skeleton-reveal"
                                        style={{
                                            animationDelay: `${delay}ms`,
                                            opacity
                                        }}
                                    >
                                        <div className="aspect-[3/4] bg-gray-100 rounded-2xl animate-pulse" />
                                        <div className="h-4 w-3/4 bg-gray-100 rounded-full animate-pulse" />
                                        <div className="h-4 w-1/4 bg-gray-100 rounded-full animate-pulse" />
                                    </div>
                                );
                            })}


                        </div>
                    ) : allProducts.length > 0 ? (
                        <>
                            <div className='grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 w-full gap-x-5 gap-y-10 font-montserrat'>
                                {allProducts.map((product, index) => {
                                    if (!visibleProducts.has(product.ID)) return null;

                                    return (
                                        <div
                                            key={`${product.ID}-${index}`}
                                            className='flex flex-col gap-3 group animate-fade-in cursor-pointer'
                                            onClick={() => fullimage(product.ID, product.ItemName)}
                                        >
                                            <div className='aspect-[3/4] rounded-2xl bg-gray-50 flex items-center justify-center relative overflow-hidden'>
                                                <img
                                                    src={getProductImageUrl(product)}
                                                    alt={product.ItemName}
                                                    loading="lazy"
                                                    onError={(e) => { e.target.src = product1; }}
                                                    className='w-full h-full object-cover transition-transform duration-400 group-hover:scale-110'
                                                />
                                                <div className='absolute top-4 right-4'>
                                                    <button
                                                        className='w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex justify-center items-center cursor-pointer shadow-sm hover:bg-white transition-all transform hover:scale-110 active:scale-90'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (wishlistItems.some(item => item.ID === product.ID)) {
                                                                removeFromWishlist(product.ID);
                                                            } else {
                                                                addToWishlist(product);
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={wishlistItems.some(item => item.ID === product.ID) ? filledHeart : heart}
                                                            alt="heart"
                                                            className='w-5 h-5'
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className='flex flex-col gap-1 px-1'>
                                                <PromotionTags product={product} />
                                                <h3 className='text-left text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-red-600 transition-colors uppercase'>
                                                    {product.ItemName}
                                                </h3>
                                                <div className='flex items-center gap-2'>
                                                    <span className='text-lg font-semibold text-black'>Rs. {product.MRP}</span>
                                                    {/* {product.MOP && product.MOP < product.MRP && (
                                                        <span className='text-sm text-gray-400 line-through'>Rs. {product.MOP}</span>
                                                    )} */}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Loading More Indicator */}
                            {/* Loading More Indicator */}
                            {isLoadingMore && (
                                <div className='flex flex-col items-center justify-center py-12 gap-4'>
                                    <div className='w-10 h-10 border-4 border-gray-100 border-t-black rounded-full animate-spin'></div>
                                    <span className='text-sm font-bold text-gray-400 uppercase tracking-widest'>Loading More...</span>
                                </div>
                            )}

                            {!isLoadingMore && hasMore && allProducts.length > 0 && (
                                <div className='flex justify-center py-8'>
                                    <button
                                        onClick={loadMoreProducts}
                                        className='px-8 py-3 bg-black text-white text-sm font-bold rounded-full hover:bg-gray-800 transition-all transform hover:scale-105 shadow-lg'
                                    >
                                        Load More Products
                                    </button>
                                </div>
                            )}

                            {/* End of results */}
                            {/* {!hasMore && allProducts.length > 0 && (
                                <div className='flex flex-col items-center justify-center py-20 border-t border-gray-50 mt-10'>
                                    <div className='w-12 h-1 bg-black mb-6'></div>
                                    <p className='text-lg font-bold uppercase tracking-tighter text-black'>You've seen all products</p>
                                    <p className='text-gray-400 text-sm mt-1'>Showing {totalProducts} items total</p>
                                </div>
                            )} */}

                            {/* <div className='w-full flex items-end justify-end mt-8'>
                                <span
                                    className='text-right underline cursor-pointer hover:text-red-600 text-base font-[500] font-montserrat transition-colors duration-300'
                                    onClick={searchpage}
                                >
                                    See All
                                </span>
                            </div> */}
                        </>
                    ) : (
                        <div className="flex justify-center items-center py-12">
                            <span className="text-gray-500 text-lg">No products available at the moment.</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Sections */}
            <div className="w-full h-auto lg:mt-12 mt-6 flex flex-col pb-12 gap-12">
                <div className="lg:h-[143px] h-[72px] bg-[#1A0136] w-full overflow-hidden relative flex items-center">
                    <div className="flex gap-6 animate-marquee whitespace-nowrap items-center">
                        {Array.from({ length: 15 }).map((_, index) => (
                            <React.Fragment key={index}>
                                <img src={star} alt="star" className="md:inline-block hidden" />
                                <img src={starmob} alt="starmob" className='inline-block md:hidden' />
                                <span className="text-white lg:text-2xl md:text-xl text-sm font-[600] font-montserrat inline-block">
                                    Find your perfect fit for every activity
                                </span>
                                <img src={starmob} alt="starmob" className='inline-block md:hidden' />
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className='flex lg:flex-row flex-col justify-start items-center bg-[#F6F6F6] w-full h-auto lg:gap-0 gap-3 pb-6'>
                    <div className='lg:w-[50%] w-full lg:px-[10%] px-3 flex flex-col gap-3 justify-center text-left lg:py-0 py-5'>
                        <span className='lg:text-3xl text-2xl font-[600] font-montserrat'>WE ELEVATE YOUR<br /> PERFORMANCE IN STYLE!</span>
                        <span className='lg:text-lg text-xs font-[400] font-montserrat'>"Combining cutting-edge design with ultimate<br /> comfort,
                            our sportswear keeps you moving,<br /> looking, and feeling your best-every step of the<br /> way."</span>
                        {/* <span
                            className='lg:w-[168px] w-[93px] lg:h-[46px] h-[30px] bg-black flex justify-center items-center text-white rounded-[8px] font-[600] lg:text-lg text-xs font-montserrat cursor-pointer hover:bg-red-600 transition-colors duration-300'
                            onClick={handleShopNowClick}
                        >
                            Shop Now
                        </span> */}
                    </div>
                    <div className='lg:w-max w-full h-full lg:px-0 px-3'>
                        <img src={combined} alt="combined" className='w-full h-auto' />
                    </div>
                </div>
            </div>

            {isTab ? <FooterMob /> : <Footer />}
        </div>
    );
}

export default Home;








